import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  defaultDataIdFromObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import fetch from "isomorphic-fetch";

// Ensure that the token is coming from an environment variable
const default_token = process.env.GATSBY_CRAFT_API_EXPORT_TOKEN; // Exposed to client-side with GATSBY_ prefix

// Function to add __typename based on typeHandle
function addTypenameToData(data) {
  if (Array.isArray(data)) {
    return data.map(addTypenameToData);
  } else if (data !== null && typeof data === 'object') {
    const newData = { ...data };

    // Map 'typeHandle' to '__typename'
    if (newData.typeHandle && !newData.__typename) {
      newData.__typename = newData.typeHandle;
    }

    // Recursively apply to nested objects
    for (const key in newData) {
      if (newData.hasOwnProperty(key)) {
        newData[key] = addTypenameToData(newData[key]);
      }
    }

    return newData;
  } else {
    return data;
  }
}

// Custom Apollo Link to add __typename
const typeHandleToTypenameLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    if (response.data) {
      response.data = addTypenameToData(response.data);
    }
    return response;
  });
});

/**
 * Factory function to create a new Apollo Client instance with dynamic headers.
 *
 * @param {string} token - The preview token.
 * @param {string} xCraftPreview - The X-Craft-Preview header value.
 * @param {string} xCraftLivePreview - The X-Craft-Live-Preview header value.
 * @param {string} [apiUrl] - The Url of Craft's GraphQL API URL value, otherwise uses our default.
 * @returns {ApolloClient} - Configured Apollo Client instance.
 */
export const createTopicClient = (token, xCraftPreview, xCraftLivePreview, apiUrl) => {
  // Create the HTTP link for the Apollo Client
  console.log('--- createTopicClient Invocation ---');
  console.log('apiUrl:', apiUrl, 'Type:', typeof apiUrl);
  console.log('GATSBY_CRAFT_API_IMPORTEXPORT_URL:', process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL, 'Type:', typeof process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL);
  
  const uri = apiUrl ?? process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL;
  
  console.log('Assigned uri:', uri, 'Type:', typeof uri);
  const httpLink = createHttpLink({
    uri, // The API endpoint (replace as needed)
    fetch,
  });
  console.log("apiUrl:",apiUrl);
  console.log("uri:",uri);
  console.log("url",process.env.GATSBY_CRAFT_API_IMPORTEXPORT_URL);
  console.log("httpLink:",httpLink);
  // Middleware to set the headers dynamically
  // X-Craft-Token / X-Craft-Authorization are Craft preview-draft tokens and
  // must only be sent when a real preview token is present. Craft 5.8+ rejects
  // requests that send a plain GraphQL bearer token in X-Craft-Token (400).
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : `Bearer ${default_token}`,
      ...(token ? {
        'X-Craft-Authorization': `Bearer ${token}`,
        'X-Craft-Token': token,
      } : {}),
    },
  }));

  // Combine the links in the correct order
  const link = ApolloLink.from([
    authLink,
    typeHandleToTypenameLink, // Place response transformer before terminating link
    httpLink,
  ]);

  // GOCHA: This means siteId needs to be returned with all entry queries, else there will be cache conflicts:
  const cache = new InMemoryCache({
    dataIdFromObject(obj) {
      // only attach IDs to objects that have a typename AND an id
      if (obj && obj.__typename && obj.id !== undefined) {
        const site = obj.siteId ?? 1; // ← default locale
        return `${obj.__typename}:${obj.id}:${site}`;
      }
      // defer to Apollo's default for everything else
      return defaultDataIdFromObject(obj);
    },
  });

  // Create the Apollo Client instance
  const client = new ApolloClient({
    link: link,
    cache,
    // GOTCHA: empty possibleTypes is not recommended as 'it can lead to unexpected behavior and makes Apollo Client less efficient'.
  });

  return client;
};